import type { VoidFunctionComponent } from 'react';

import Subnav from '../../generic/Subnav';
import type { ProjectDependencyScope } from '../../../../shared/dependenciesV2';

import { useInventoryDependencyCounts } from './hooks/useInventoryDependencyCounts';
import { useInventoryFeaturesForProject } from './hooks/useInventoryFeaturesForProject';
import InventorySubnavDropdown from './InventorySubnavDropdown';

type Props = {
  /**
   * The base URL for either this project revision.
   */
  baseUrl: string;
  /**
   * The scope of the inventory dropdown, used to determine counts.
   */
  scope: ProjectDependencyScope;
  /**
   * This is used to hold off loading the counts until the
   * parent data has loaded.
   */
  isLoading: boolean;
  /**
   * Optional fallback project revision locator used when the route does not include revision_id.
   * This happens for routes like Activity and Settings, which are outside the revision container context.
   */
  fallbackProjectRevisionLocator?: string | null;
};

/**
 * InventorySubnavItem contains the subnav item for the "Inventory" section.
 * For orgs where Snippet Scanning is not enabled, this is basically just the old Dependencies subnav item.
 * For orgs where Snippet Scanning is enabled, this is the Inventory dropdown.
 */
const ProjectSubnavItemInventory: VoidFunctionComponent<Props> = ({
  baseUrl,
  scope,
  isLoading: isLoadingParentData,
  fallbackProjectRevisionLocator,
}) => {
  const features = useInventoryFeaturesForProject();
  const { isLoading: areDependencyCountsLoading, counts } = useInventoryDependencyCounts({
    scope,
    features,
    shouldFetch: !isLoadingParentData,
    fallbackProjectRevisionLocator: fallbackProjectRevisionLocator ?? null,
  });
  const {
    shouldShowInventory,
    shouldShowSnippets,
    shouldShowBinaryAnalysis,
    shouldShowVendored,
    isInventoryUIFeatureEnabled,
    isLoading: areFeaturesLoading,
  } = features;

  //  Just show the one Dependencies subnav item if the inventory dropdown is not enabled
  if (!isInventoryUIFeatureEnabled || (!areFeaturesLoading && !shouldShowInventory)) {
    return (
      <Subnav.ItemWithCount
        to={`${baseUrl}/browse/dependencies`}
        navKey="browse/dependencies"
        count={counts.managed ?? 0}
        isLoading={areDependencyCountsLoading.managed}
      >
        Dependencies
      </Subnav.ItemWithCount>
    );
  }

  return (
    <InventorySubnavDropdown
      baseUrl={baseUrl}
      isLoading={areDependencyCountsLoading}
      counts={counts}
      shouldShowSnippets={shouldShowSnippets}
      shouldShowBinaryAnalysis={shouldShowBinaryAnalysis}
      shouldShowVendored={shouldShowVendored}
    />
  );
};

export default ProjectSubnavItemInventory;
